/**
 * Inline sharing-form logic (topic_garden only, gated by `topicGardenEnabled`).
 *
 * The inline dialect is the authoring layer for the per-type
 * `default_share_form.yaml` and a garden topic's saved `customTemplates`: the
 * document is a `forms` list, one entry per intent, and each entry's `form` is
 * an ordered sequence of SLOTS defined INLINE at their point of use. Each slot
 * carries a `type` tag (`text`, `goal`, `optional`, `select`, `fillIn`) taken
 * from its YAML key, so every function here dispatches on the tag rather than
 * on the slot's shape. A `select` offers preset `options` plus an optional
 * `writeIn` — the free-text alternative the end user can type instead of
 * picking one.
 *
 * Two roles touch a form. The topic manager authors this layout — free to edit
 * every slot of it, including the logo and share-link primitives. The end user,
 * when sharing, can only fill the `select` / `fillIn` slots; text is static in
 * the sent message, primitives and all (they resolve at compose time and
 * render as fixed literal text, never an input).
 *
 * This module parses, validates and dumps the form. Messages are composed from
 * the form itself by `ComposeLogic`. The zod schema that validates the
 * structure lives alongside in `./schema`.
 */

import { ZodError } from 'zod';
import yaml from 'js-yaml';
import { TemplateLogic } from '@/modules/template/logic';
import type { MessageIntent } from '@/modules/template/types';
import { SharingConfigSchema, sharingConfigSchema } from './schema';
import type { TopicTypeId } from '@/modules/topic/types';
import { SlotLogic } from './slot_logic';
import type {
  AssembleResult,
  SharingFormSlot,
  SharingConfig,
} from './types';

/**
 * The impression-goal prose a sharing form carries: the text of its first
 * `goal` element in document order, trimmed of the paragraph-break whitespace
 * the slot owns inside the body. A goal slot is an ordinary form element, so any
 * topic type may carry one. '' when the YAML is missing, is not a sharing setup
 * config, or declares no goal element.
 *
 * This is the one standalone source of the sentence: surfaces that show it
 * outside a composed message (the news welcome sheet's progress line, the
 * future-child preview) render it from here, while a composed message gets it
 * from its goal slot like any other text.
 */
function goalProse(yamlSource: string | null | undefined): string {
  if (!yamlSource) return '';
  const parsed = SharingConfigSchema.safeParse(yaml.load(yamlSource)).data;
  for (const form of parsed?.forms ?? []) {
    for (const slot of form.form) {
      if (slot.type === 'goal') return slot.expr.trim();
    }
  }
  return '';
}

// ── Editor helpers (operate on the raw inline structure) ────────────────────

/**
 * Validated raw sharing config — the tagged authoring shape the builder
 * edits and the composer reads — for a topic of `type`, whose definitions
 * its slots may read (see `sharingConfigSchema`).
 */
function parseSharingConfigRaw(
  yamlSource: string,
  type?: TopicTypeId
): SharingConfig {
  const parsed = sharingConfigSchema(type).parse(yaml.load(yamlSource));
  return {
    version: 1,
    forms: parsed.forms.map(f => ({ intent: f.intent, form: [...f.form] })),
  };
}

const DUMP_OPTS: yaml.DumpOptions = { indent: 2, lineWidth: 80, noRefs: true };

/**
 * Returns the form as the list of YAML nodes it is authored as: each slot
 * dumps under its own type key, its id included. The exact inverse of the
 * schema's `withTypeTag`.
 */
function slotsForDump(form: readonly SharingFormSlot[]): unknown[] {
  return form.map(slot => ({ [slot.type]: withoutTag(slot) }));
}

/** A slot's payload: everything it carries except the tag itself. */
function withoutTag(slot: SharingFormSlot): Record<string, unknown> {
  const payload: Record<string, unknown> = { ...slot };
  delete payload.type;
  return payload;
}

function formatError(err: unknown): string {
  if (err instanceof ZodError) {
    return err.issues
      .map(issue => {
        const path = issue.path.join('.');
        return path ? `${path}: ${issue.message}` : issue.message;
      })
      .join('\n');
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

/** Parse a sharing config, returning a readable error instead of throwing. */
function validateSharingConfig(
  yamlSource: string,
  type?: TopicTypeId
): { ok: true } | { ok: false; error: string } {
  try {
    parseSharingConfigRaw(yamlSource, type);
    return { ok: true };
  } catch (err) {
    return { ok: false, error: formatError(err) };
  }
}

/**
 * Reassemble from each form's WHOLE element sequence — the flat slot list the
 * builder edits. Nothing is re-wrapped or reordered here; this just dumps and
 * validates. Skipping the text round-trip means a slot the author never touched
 * is re-emitted exactly as it was parsed.
 */
function assembleSharingConfigYaml(
  config: SharingConfig,
  forms: readonly (readonly SharingFormSlot[])[],
  type?: TopicTypeId
): AssembleResult {
  const formsOut = config.forms.map((f, j) => ({
    intent: f.intent,
    form: slotsForDump(forms[j] ?? f.form),
  }));

  const fullYaml = yaml.dump({ version: 1, forms: formsOut }, DUMP_OPTS);
  const validation = validateSharingConfig(fullYaml, type);
  if (!validation.ok) return { ok: false, error: validation.error };
  return { ok: true, yaml: fullYaml };
}

/**
 * Whether the YAML is a currently-valid sharing config (the inline dialect,
 * not the classic one). Used to decide whether a topic's saved `customTemplates`
 * can seed the editor, or whether to fall back to the type default — e.g. a
 * payload saved in a format the parser no longer accepts reads as not-a-config.
 */
function isSharingConfig(yamlSource: string, type?: TopicTypeId): boolean {
  return validateSharingConfig(yamlSource, type).ok;
}

/**
 * The editor's title for one intent's form. The builder shows one intent at a
 * time, so the screen is named for the form on it rather than for the modal.
 * `intro` is the form recipients are first shared with, so it reads as the
 * sharing form; anything else is named after its intent.
 */
function intentFormTitle(intent: MessageIntent): string {
  return intent === 'intro'
    ? 'Sharing Form'
    : `${TemplateLogic.intentLabel(intent)} Form`;
}

/**
 * Every variable the config's slots assign, across all its forms, sorted and
 * without duplicates. There is no registry of variables: a variable exists
 * because some slot assigns it, so this is what the builder's variable picker
 * offers. A slot whose variable is not named yet contributes nothing.
 */
function variableNames(config: SharingConfig): string[] {
  const names = new Set<string>();
  for (const form of config.forms) {
    for (const slot of form.form) {
      const name = SlotLogic.declaredVariableName(slot);
      if (name) names.add(name);
    }
  }
  return Array.from(names).sort((a, b) => a.localeCompare(b));
}

/** Pure logic for the inline sharing-form dialect: parsing + editor helpers. */
export const FormLogic = {
  goalProse,
  parseSharingConfigRaw,
  assembleSharingConfigYaml,
  validateSharingConfig,
  isSharingConfig,
  intentFormTitle,
  variableNames,
};
